import { FC, useState, createContext, useContext, useEffect } from 'react'
import * as authHelper from './AuthHelpers'
import { WithChildren } from '../../../../_metronic/helpers'
import {
  removeItem,
  setAccounts,
  setActiveAccount,
  setUserInvestors,
  setUserProjects,
  setUserRoles,
} from '../../../../utils/SessionStorageUtils'
import { usePortfolioStore } from '../../../../store/portfolioFilterDataStore'
import { useAuthorizationStore } from '../../../../store/useAuthStore'
import { useLatestActivityStore } from '../../../../store/latestActivityDateStore'
import { useUserNameStore } from '../../../../store/UserNameStore'
import { useRefetchAccessToken, useUserContextQuery } from './_requests'
import { getAuth } from './AuthHelpers'
import { useCompanyNameStore } from '../../../../store/companyNameStore'
import Toast, { ToastType } from '../../../../utils/toast'

type AuthContextProps = {
  auth: string | undefined
  saveAuth: (auth: string | undefined, defaultAccount: boolean) => void
  logout: () => void
  isDefaultAccount: boolean
  role: string | undefined
  saveRole: (auth: string | undefined) => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  logout: () => {},
  isDefaultAccount: false,
  role: authHelper.getUserRole(),
  saveRole: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<string | undefined>(authHelper.getAuth())
  const [isDefault, setIsDefault] = useState<boolean>(false)

  const useFilterStore = usePortfolioStore()
  const useActivityStore = useLatestActivityStore()
  const userStore = useAuthorizationStore()
  const userNameStore = useUserNameStore()
  const companyNameStore = useCompanyNameStore()

  const [role, setRole] = useState<string | undefined>(authHelper.getUserRole())
  const userAuthStore = useAuthorizationStore()
  let trigger = !role
  const isPublicPath =
    window.location.pathname.includes('documentViewer/') ||
    window.location.pathname.includes('publicInvestmentModelling/')
  const { data, isError } = useRefetchAccessToken(trigger)
  const {
    isFetched,
    data: userContextData,
    isError: isUserContextError,
  } = useUserContextQuery(getAuth()! ?? '', !role && !isPublicPath)

  if (isError) {
    const redirectUrl = encodeURIComponent(window.location.href)
    let baseUrl = (
      process.env.REACT_APP_ID_SERVER_CLIENT_URL ?? 'https://dev.identity.hissa.com'
    ).replace(/\/+$/, '')
    const url = `${baseUrl}/login?redirect=${redirectUrl}&platform=investor`

    window.location.href = url
  }

  useEffect(() => {
    if (data) {
      saveAuth(data.data, true)
    }
  }, [data])
  useEffect(() => {
    if (userContextData) {
      saveRole(userContextData)
    }
  }, [isFetched])

  useEffect(() => {
    // Only handle user context error after the query has been attempted and has an auth token
    if (isUserContextError && isFetched && auth) {
      sessionStorage.clear()
      const redirectingURl =
        `${process.env.REACT_APP_ID_SERVER_CLIENT_URL}/landing/explore/investor` || ''
      window.location.href = redirectingURl
    }
  }, [isUserContextError, isFetched, auth])

  const saveAuth = async (auth: string | undefined, isDefaultBoolean: boolean) => {
    try {
      setAuth(auth)
      setIsDefault(isDefaultBoolean)

      if (auth) {
        // Enable the query and fetch user context
        authHelper.setAuth(auth)
      } else {
        // Handle logout or token removal
        authHelper.removeAuth()
      }
    } catch (error) {
      console.error('Failed to fetch user context:', error)
    }
  }

  const saveRole = (data: any) => {
    try {
      if (Array.isArray(data?.investorTypes) && data.investorTypes.length === 0) {
        const accountName = data?.activeAccount?.name
        Toast(
          accountName
            ? `No investor is associated with "${accountName}". Please add an investor to this account.`
            : 'No investor is associated with this account. Please add an investor to continue.',
          ToastType.error
        )
      }

      setUserRoles('userRoles', data.actions)
      userAuthStore.setUserRoles(JSON.stringify(data.actions))
      userAuthStore.setUserInvestors(JSON.stringify(data.investorTypes))
      setUserInvestors('userInvestors', data.investorTypes)
      sessionStorage.setItem('userCompanies', JSON.stringify(data.userCompanies))
      sessionStorage.setItem('isAccountAdmin', data?.isAccountAdmin)
      sessionStorage.setItem('isSuperAdmin', data?.superAdmin ?? false)
      sessionStorage.setItem('datasets', JSON.stringify(data.datasets || []))
      setAccounts(data.accounts || [])
      setActiveAccount(data.activeAccount || null)
      setUserProjects('userProjects', data.projects)
      setRole(data.actions)
    } catch (error) {
      console.error('Failed to save role:', error)
    }
  }

  const logout = () => {
    saveAuth(undefined, false)
    removeItem('userRoles')
    removeItem('userInvestors')
    removeItem('userPreferences')
    useFilterStore.reset()
    useActivityStore.reset()
    userNameStore.reset()
    companyNameStore.reset()
    userStore.clearAuthorization()
    sessionStorage.clear()
  }
  const isDefaultAccount = isDefault
  return (
    <AuthContext.Provider
      value={{ auth, saveAuth, logout, isDefaultAccount: isDefaultAccount, role, saveRole }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthProvider, useAuth }
